import type { User } from "firebase/auth";
import { type FormEvent, useEffect, useRef, useState } from "react";
import * as api from "./api";
import { messageMailText } from "./mailSettings";
import { SkeletonSection } from "./Skeleton";

/** SO-1474: who gets a mail when a customer sends a message in the portal. */
export function MessageMailSettings({ user }: { user: User }) {
  const [found, setFound] = useState<api.MessageMail | "failed" | "none" | undefined>(undefined);
  const [mode, setMode] = useState<api.MessageMailMode>("salesperson");
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const status = useRef<HTMLParagraphElement>(null);

  const show = (next: api.MessageMail) => {
    setFound(next);
    setMode(next.mode);
  };

  useEffect(() => {
    user
      .getIdToken()
      .then(api.messageMail)
      .then(show, (e: unknown) => {
        // An older API has no route: render no section.
        console.warn("message mail: load failed", e);
        setFound(e instanceof api.ApiError && e.status === 404 ? "none" : "failed");
      });
  }, [user]);

  if (found === undefined) return <SkeletonSection title="Message mail" rows={4} />;
  if (found === "none") return null;
  if (found === "failed") {
    return (
      <fieldset>
        <legend>Message mail</legend>
        <p className="error">Cannot load the message mail. Try again later.</p>
      </fieldset>
    );
  }
  const save = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setMessage(null);
    const started = performance.now();
    try {
      const next = await api.setMessageMail(await user.getIdToken(), mode);
      console.info(`message mail: saved in ${Math.round(performance.now() - started)} ms`);
      show(next);
      setMessage({ ok: true, text: `Saved. ${messageMailText(next.mode)}` });
      requestAnimationFrame(() => status.current?.focus());
    } catch (e) {
      console.warn("message mail: save failed", e);
      const detail = e instanceof api.ApiError && e.status < 500 ? e.detail : undefined;
      setMessage({ ok: false, text: detail ?? "The message mail was not saved. Try again." });
    } finally {
      setSaving(false);
    }
  };
  return (
    <form onSubmit={save} noValidate data-testid="message-mail">
      <fieldset disabled={!found.can_edit || saving}>
        <legend>Message mail</legend>
        {!found.can_edit ? (
          <p data-testid="message-mail-current">
            <strong>{found.choices.find((choice) => choice.mode === found.mode)?.label ?? found.mode}</strong>
          </p>
        ) : (
          <div role="radiogroup" aria-describedby="message-mail-hint">
            {found.choices.map((choice) => (
              <label key={choice.mode} className="check">
                <input
                  type="radio"
                  name="message-mail"
                  value={choice.mode}
                  data-testid={`message-mail-${choice.mode}`}
                  checked={mode === choice.mode}
                  onChange={() => {
                    setMode(choice.mode);
                    setMessage(null);
                  }}
                />{" "}
                {choice.label}
              </label>
            ))}
          </div>
        )}
        <p id="message-mail-hint" className="hint">
          {messageMailText(found.mode)} The mail tells the reader not to reply to it, because a reply does not reach the customer.
          {found.can_edit ? "" : " Only an administrator can change it."}
        </p>
        {found.can_edit && (
          <div className="actions">
            <button className="primary" data-testid="save-message-mail">
              {saving ? "Saving…" : "Save the message mail"}
            </button>
          </div>
        )}
      </fieldset>
      <p ref={status} tabIndex={-1} role="status" data-testid="message-mail-status" className={message && !message.ok ? "error" : undefined}>
        {message?.text}
      </p>
    </form>
  );
}
